<template>
  <div>
    <!-- Use v-show here so it keeps the values for make and model -->
    <div v-show="!showFoundVehicles" class="form-vehicle-search-container flex flex-col gap-6 pb-6">
      <PanelTitle :icon="context.icon" :title="context.title" :subTitle="context.subTitle" />

      <!-- Use v-if here to bypass rego validation on submit step -->
      <FindCarByRego
        v-if="!showFoundVehicles"
        :findCarBtn="context.findCarBtn.value"
        :regoInputLabel="context.regoInputLabel.value"
        :help="context.isRoadside?.value ? context.roadsideRegoHelpText?.value : ''"
        :regoNumber="vehicleRegistration"
        :regoTooltip="regoTooltip"
        :showManualOverrideInfo="findCarStore.showManualVehicleDetailsToEnter"
        :lookUpVehicleManualInfo="context.lookUpVehicleManualInfoMessage?.value ?? ''"
        :isRoadside="context.isRoadside?.value"
        @vehicleFound="handleVehicleFoundFromRego"
        @qldVehicleNotFound="setQldRegoFound(false)"
        @setRegistration="(data: string) => setRegistration(data)"
      />
      <ShowHideButton
        v-if="showExpandButton"
        :showMore="showMakeAndModel"
        :showLessLabel="context.expandMakeAndModelLabel?.value"
        :showMoreLabel="context.expandMakeAndModelLabel?.value"
        appendClass="!mt-0"
        @click="toggleMakeAndModelSection"
      />

      <div v-if="showMakeAndModel" class="flex flex-col gap-8 md:gap-6">
        <RichText v-if="showExpandButton" :field="context.makeAndModelTitleMessage" />
        <FormKit v-slot="{ state: { valid } }" type="form" @submit="showFoundVehicleList">
          <FindYourCar
            ref="findYourCarEl"
            :topMakes="context.topMakes.value"
            :colourLabel="context.colourLabel.value"
            :yearLabel="context.yearLabel.value"
            :makeLabel="context.makeLabel.value"
            :modelLabel="context.modelLabel.value"
            :seriesLabel="context.seriesLabel.value"
            :bodyLabel="context.bodyLabel.value"
            :showColour="context.showColour.value"
            :isRoadside="context.isRoadside.value"
            @modelMakeChanged="findCarStore.resetFoundVehicleList"
            @vehicleFound="(data: any) => updateFoundVehicleList(data)"
            @setColour="(data: string) => setVehicleColour(data)"
          />

          <div class="h-12 mt-6 md:h-min flex items-start">
            <FormKit
              :label="context.findCarBtn.value"
              type="submit"
              :disabled="!valid || foundVehiclesByRego || !foundVehicleList.length"
              :inputClass="`btn btn-primary md:text-left`"
            />
          </div>
        </FormKit>
      </div>
    </div>

    <div v-if="showFoundVehicles" class="mb-6 flex flex-col gap-6">
      <FindYourCarList
        :regoResultsStepTitle="context.regoResultsStepTitle.value"
        :regoResultsStepContent="context.regoResultsStepContent.value"
        :makeAndModelResultsStepTitle="context.makeAndModelResultsStepTitle.value"
        :makeAndModelResultsStepContent="context.makeAndModelResultsStepContent.value"
        :vehicleList="foundVehicleList"
        :foundVehiclesByRego="foundVehiclesByRego"
        :showReleaseDate="context.showReleaseDate?.value ?? false"
        @vehicleSelected="selectVehicle"
      />

      <div v-if="showColourOnListView">
        <RACQFormKit
          v-model="vehicleColourOnListView"
          :bindings="{
            help: context.colourFieldOnListHelpText.value || '',
            innerClass: 'input-sm',
            label: 'Vehicle colour',
            type: 'text',
            validation: [['required', 'trim']],
            validationMessages: {
              required: context.colourFieldOnListValidationText?.value || 'Please enter a vehicle colour',
            },
            maxlength: 100,
          }"
        />
      </div>

      <RichText v-if="!qldRegoFound && showFoundVehicles" :field="context.regoNotFoundMessage" />

      <!-- ShowHideButton shown for roadside override or expand toggle -->
      <ShowHideButton
        v-if="foundVehiclesByRego"
        :showMore="showMakeAndModel"
        :showLessLabel="context.expandMakeAndModelManualLabel?.value"
        :showMoreLabel="context.expandMakeAndModelManualLabel?.value"
        :showMorePreCopy="context.expandMakeAndModelPreLabel?.value"
        :isDropDownVariant="false"
        appendClass="!mt-0"
        @click="openMakeAndModelSection"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import FindCarByRego from '@/components/FindCar/FindCarByRego.vue';
import FindYourCarList from '@/components/FindCar/FindYourCarList.vue';
import FindYourCar from '@/components/FindCar/FindYourCar.vue';
import { useAnalyticsStore, useJeopardyStore, useFindCarStore, useFormStore } from '@/store';
import type { IFoundVehicle } from '@/interfaces';
import ShowHideButton from '@/components/ShowHideButton/ShowHideButton.vue';
import { storeToRefs } from 'pinia';
import { Field, RichText } from '@sitecore-jss/sitecore-jss-vue';
import { ImageFieldValue } from '@sitecore-jss/sitecore-jss-vue/types/components/Image';
import PanelTitle from '@/components/PanelTitle/PanelTitle.vue';
import { FormEvents } from '@/enums/form-events.enum';
import { isMobileDevice, scrollToElement } from '@/utils/dom-helpers';
import { IAnalyticsProfileInfo } from '@/interfaces/forms/analytics-interface';
import { clone } from 'lodash-es';
import RACQFormKit from '@/components/RACQFormKit/RACQFormKit.vue';

interface IFindCarProps {
  node?: any;
  value?: any;
  topMakes: { value: string };
  regoResultsStepTitle: { value: string };
  regoResultsStepContent: { value: string };
  makeAndModelResultsStepTitle: { value: string };
  makeAndModelResultsStepContent: { value: string };
  findCarBtn: { value: string };
  regoNotFoundMessage: { value: string };
  yearLabel: { value: string };
  makeLabel: { value: string };
  modelLabel: { value: string };
  seriesLabel: { value: string };
  bodyLabel: { value: string };
  colourLabel: { value: string };
  regoInputLabel: { value: string };
  showColour: { value: boolean };
  showColourFieldOnList: { value: boolean };
  colourFieldOnListHelpText: { value: string };
  colourFieldOnListValidationText: { value: string };
  makeAndModelTitleMessage: { value: string };
  expandMakeAndModelLabel: { value: string };
  expandMakeAndModelManualLabel: { value: string };
  expandMakeAndModelPreLabel: { value: string };
  icon: Field<ImageFieldValue>;
  title: { value: string };
  subTitle: { value: string };
  roadsideRegoHelpText: { value: string };
  isRoadside: { value: boolean };
  formName: { value: string };
  tooltipContent: { value: string };
  tooltipModalTitle: { value: string };
  tooltipShowInModal: { value: boolean };
  profileInfo: IAnalyticsProfileInfo;
  lookUpVehicleManualInfoMessage: { value: string };
  showReleaseDate: { value: boolean };
}

// Define Props
// ------------------------------------------------------------- //
const props = defineProps<{ context: IFindCarProps }>();

// Define Composables
// ------------------------------------------------------------- //
const analyticsStore = useAnalyticsStore();
const jpStore = useJeopardyStore();
const findCarStore = useFindCarStore();
const formStore = useFormStore();

// Define Component state
// ------------------------------------------------------------- //
const findYourCarEl = ref<typeof FindYourCar | undefined>(undefined);
const qldRegoFound = computed(() => findCarStore.isQldRegoFound);
const foundVehiclesByRego = ref(true);
const isExpanded = ref(false);

const vehicleRegistration = computed(() => findCarStore.registrationNumber);
const showMakeAndModel = computed(() => !qldRegoFound.value || isExpanded.value);
const showFoundVehicles = computed(() => findCarStore.showFoundVehicles);
const showExpandButton = computed(
  () => props.context.expandMakeAndModelLabel?.value && !props.context.isRoadside.value && qldRegoFound.value
);
const regoTooltip = computed(() => ({
  title: props.context?.tooltipModalTitle?.value,
  content: props.context?.tooltipContent?.value,
  tooltipId: props.context?.id + '__tooltip_id',
  tooltipOuterId: props.context?.id + '__outer',
  isModal: props.context?.tooltipShowInModal?.value,
}));
const showColourOnListView = computed(() => props.context.showColourFieldOnList?.value ?? false);
const vehicleColourOnListView = ref(findCarStore.vehicleColour);

const { selectedVehicle, foundVehicleList } = storeToRefs(findCarStore);

findCarStore.setFormName(props.context.formName.value);

// Define functions
// ------------------------------------------------------------- //
// Sync store to ref (when store updates externally)
watch(
  () => findCarStore.vehicleColour,
  (newValue: string) => {
    vehicleColourOnListView.value = newValue;
  }
);
// Sync ref to store (when user types in the input)
watch(vehicleColourOnListView, (newValue: string) => {
  findCarStore.vehicleColour = newValue;
});

function setQldRegoFound(isFound: boolean) {
  findCarStore.isQldRegoFound = isFound;
}

function setRegistration(registrationNumber: string) {
  findCarStore.registrationNumber = registrationNumber;
}

function setVehicleColour(vehicleColour: string) {
  findCarStore.vehicleColour = vehicleColour;
}

function setManualVehicleDetailsEnter(setManual: boolean) {
  findCarStore.showManualVehicleDetailsToEnter = setManual;
}

function updateFoundVehicleList(data: IFoundVehicle[], byRego?: boolean) {
  selectedVehicle.value = undefined;
  // Validate incoming data
  if (!Array.isArray(data)) {
    foundVehicleList.value = [];
  } else {
    foundVehicleList.value = data;
  }
  foundVehiclesByRego.value = byRego || false;
  findCarStore.vehicleFoundUsingRego = byRego ?? false;
  if (findCarStore.showManualVehicleDetailsToEnter) {
    isExpanded.value = false;
  }
}

function handleVehicleFoundFromRego(data: IFoundVehicle) {
  updateFoundVehicleList(data ? [data] : [], true);
  showFoundVehicleList();
  setQldRegoFound(true);
  isExpanded.value = false; // Hide make and model if vehicle found by rego
}

const toggleMakeAndModelSection = () => {
  if (!qldRegoFound.value) {
    isExpanded.value = true;
    return;
  }
  isExpanded.value = !isExpanded.value;
};

const openMakeAndModelSection = () => {
  isExpanded.value = true;
  findCarStore.resetFoundVehicleList();
  setQldRegoFound(false);
  setManualVehicleDetailsEnter(true);
};

function showFoundVehicleList() {
  formStore.clearLastStepSubmitted();
  if (foundVehicleList.value.length && foundVehicleList.value[0].manufacturingYear >= 1960) {
    findCarStore.showCarList(true);
    isMobileDevice() ? scrollToElement('body') : scrollToElement('.find-your-car-list', 500);

    /** Push event for analysis */
    if (foundVehicleList.value.length) {
      analyticsStore.pushAnalytics({
        event: FormEvents.STEP,
        form: {
          field: foundVehiclesByRego.value ? 'Found car registration' : 'Found car make and model',
          stepName: 'Find car confirmation',
          name: props.context.formName.value,
        },
        profileInfo: clone(props.context.profileInfo) || {},
      });
    }

    return;
  }

  jpStore.showJeopardyPanel({
    jpCode: findCarStore.pre1960VehicleJeopardyCode,
    areNextStepsVisible: false,
    formAnalyticsOverride: { formErrorDetail: 'We need to talk to you about your vehicle' },
  });
}

function selectVehicle(vehicle: IFoundVehicle) {
  findCarStore.selectedVehicle = vehicle;
}
</script>
